import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';
export type ViewMode = 'notebook' | 'worksheet';
export type RightTab = 'columns' | 'history' | 'fleet';

interface UiState {
  theme: Theme;
  objectExplorerOpen: boolean;
  columnExplorerOpen: boolean;
  viewMode: ViewMode;
  rightTab: RightTab;
  paletteOpen: boolean;
  /** Column currently pinned open in the Column Explorer detail view. */
  focusedColumn: string | null;

  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  toggleObjectExplorer: () => void;
  toggleColumnExplorer: () => void;
  toggleViewMode: () => void;
  setRightTab: (tab: RightTab) => void;
  setPaletteOpen: (open: boolean) => void;
  setFocusedColumn: (column: string | null) => void;
}

function initialTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  try {
    // Read by the inline script in index.html so the shell never flashes the
    // wrong theme before React hydrates.
    window.localStorage.setItem('duckdb.theme', theme);
  } catch {
    /* Private mode / storage disabled -- the in-memory theme still applies. */
  }
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: initialTheme(),
      objectExplorerOpen: true,
      columnExplorerOpen: true,
      viewMode: 'notebook',
      rightTab: 'columns',
      paletteOpen: false,
      focusedColumn: null,

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        set({ theme: next });
      },
      toggleObjectExplorer: () => set((s) => ({ objectExplorerOpen: !s.objectExplorerOpen })),
      toggleColumnExplorer: () => set((s) => ({ columnExplorerOpen: !s.columnExplorerOpen })),
      toggleViewMode: () =>
        set((s) => ({ viewMode: s.viewMode === 'notebook' ? 'worksheet' : 'notebook' })),
      setRightTab: (rightTab) => set({ rightTab, columnExplorerOpen: true }),
      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      setFocusedColumn: (focusedColumn) => set({ focusedColumn }),
    }),
    {
      name: 'duckdb.ui',
      partialize: (state) => ({
        theme: state.theme,
        objectExplorerOpen: state.objectExplorerOpen,
        columnExplorerOpen: state.columnExplorerOpen,
        viewMode: state.viewMode,
        rightTab: state.rightTab,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);
